import {
  EntityLinks,
  Goal,
  ID,
  LifeArea,
  lifeAreaOtherID,
  Task,
} from '@griply/types';

import { buildGoalsById, getGoalLifeAreas } from './get-goal-life-area';

export type GroupedTasks = {
  lifeAreaId: ID;
  lifeArea: LifeArea | null;
  tasks: Task[];
};

export type GroupTasksByLifeAreaOptions = {
  tasks: Task[];
  lifeAreas: LifeArea[];
  lifeAreaSorting?: ID[] | null;
  // Resolves each linked goal's life areas; without it only the task's own links count.
  goals?: Goal[];
  links?: EntityLinks;
};

// A task is listed under every life area it reaches: through each of its goals, and directly.
export const groupTasksByLifeArea = (
  options: GroupTasksByLifeAreaOptions,
): GroupedTasks[] => {
  const { tasks, lifeAreas, lifeAreaSorting, goals, links } = options;
  const goalsById = buildGoalsById(goals ?? []);
  const lifeAreaCache = new Map<ID, ID[]>();

  const groupMap = new Map<ID, Task[]>(
    lifeAreas.map(({ id }) => [id, [] as Task[]]),
  );
  groupMap.set(lifeAreaOtherID, []);

  tasks.forEach((task) => {
    const viaGoals = task.goalIds.flatMap((goalId) => {
      const goal = goalsById.get(goalId);
      return goal
        ? getGoalLifeAreas(goal, { goalsById, links, cache: lifeAreaCache })
        : [];
    });
    const targetIds = new Set(
      [...viaGoals, ...task.lifeAreaIds].filter((id) => groupMap.has(id)),
    );

    if (!targetIds.size) {
      groupMap.get(lifeAreaOtherID)?.push(task);
      return;
    }
    targetIds.forEach((id) => groupMap.get(id)?.push(task));
  });

  const nonEmptyGroups = Array.from(groupMap.entries()).reduce<GroupedTasks[]>(
    (acc, [lifeAreaId, taskList]) => {
      if (!taskList.length) {
        return acc;
      }
      acc.push({
        lifeAreaId,
        lifeArea: lifeAreas.find(({ id }) => id === lifeAreaId) ?? null,
        tasks: taskList,
      });
      return acc;
    },
    [],
  );

  // 4. Sort groups by lifeAreaSorting order
  if (lifeAreaSorting?.length) {
    nonEmptyGroups.sort((a, b) => {
      // "Other" always last
      if (a.lifeAreaId === lifeAreaOtherID) {
        return 1;
      }
      if (b.lifeAreaId === lifeAreaOtherID) {
        return -1;
      }

      const indexA = lifeAreaSorting.indexOf(a.lifeAreaId);
      const indexB = lifeAreaSorting.indexOf(b.lifeAreaId);

      // Items not in sorting array go to end (before "Other")
      if (indexA === -1 && indexB === -1) {
        return 0;
      }
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }

      return indexA - indexB;
    });
  }

  return nonEmptyGroups;
};
